import { defineStore } from 'pinia'
import { ref, watch, computed } from 'vue'
import type { SupportedLocale } from '@/i18n'

export type ThemePreference = 'light' | 'dark' | 'system'
export type EffectiveTheme = 'light' | 'dark'

/**
 * UI-State (Theme, Locale, Sidebar) — wird in localStorage persistiert
 * und nach Login mit dem User-Profil im Backend abgeglichen.
 *
 * Strikt nur UI-State — keine Pricing-, Cart- oder Validierungs-Logik.
 */
export const useUiStore = defineStore('ui', () => {
  const locale = ref<SupportedLocale>(
    (localStorage.getItem('partroom.locale') as SupportedLocale) || 'de'
  )

  const themePref = ref<ThemePreference>(
    (localStorage.getItem('partroom.theme') as ThemePreference) || 'system'
  )

  const sidebarCollapsed = ref<boolean>(localStorage.getItem('partroom.sidebarCollapsed') === '1')

  // Mobile-Drawer-State (nicht persistiert — beim Reload immer geschlossen)
  const mobileDrawerOpen = ref<boolean>(false)

  const systemDark = ref<boolean>(
    window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  )

  // System-Theme beobachten
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', (e) => (systemDark.value = e.matches))
  }

  const effectiveTheme = computed<EffectiveTheme>(() =>
    themePref.value === 'system' ? (systemDark.value ? 'dark' : 'light') : themePref.value
  )

  // Theme aufs <html> mappen, damit Tailwind-`dark`-Variant und CSS-Variablen greifen
  watch(
    effectiveTheme,
    (mode) => {
      document.documentElement.setAttribute('data-theme', mode)
    },
    { immediate: true }
  )

  watch(locale, (l) => {
    localStorage.setItem('partroom.locale', l)
    document.documentElement.setAttribute('lang', l)
  })

  watch(themePref, (t) => localStorage.setItem('partroom.theme', t))
  watch(sidebarCollapsed, (c) => localStorage.setItem('partroom.sidebarCollapsed', c ? '1' : '0'))

  return {
    locale,
    themePref,
    effectiveTheme,
    sidebarCollapsed,
    mobileDrawerOpen,
    toggleSidebar: () => (sidebarCollapsed.value = !sidebarCollapsed.value),
    toggleMobileDrawer: () => (mobileDrawerOpen.value = !mobileDrawerOpen.value),
    closeMobileDrawer: () => (mobileDrawerOpen.value = false)
  }
})
